<script lang="ts" setup>
import type { SubjectCode } from '~/models/Subject'
import type { GradeCode } from '~/models/Grade'
import type { ContentPackage } from '~/models/Content/ContentPackage'
import type { BaseItem } from '~/models/Content/BaseItem'
import { useRouter }  from '#imports'
import { useI18n } from 'vue-i18n'
import { computed, onMounted, onUnmounted } from 'vue'
import { storeToRefs } from 'pinia'
import { faXmark } from '@fortawesome/duotone-regular-svg-icons'
import { sortByGradeIndex } from '~/utils/gradeSorter'
import useProductStore from '~/stores/product'
import useLicenseControlStore from '~/stores/licenseControl'
import useFilterStore from '~/stores/filter'
import { useAuthStore } from '~/stores/auth'
import { ContentType } from '~/models/Content/ContentType'
import { useUrlBuilder } from '~/composables/useUrlBuilder'
import useSubjectView from '~/composables/useSubjectView'
import useProductHelper from '~/composables/useProductHelper'
import usePathHeaders from '~/composables/usePathHeaders'
import useFullScreen from '~/composables/useFullscreen'
import { useContentHelper } from '~/composables/useContentHelper'
import Tooltip from '~/components/ui/Tooltip.vue'
import Button from '~/components/ui/Button.vue'

const props = defineProps<{
  item?: BaseItem
  subjectCode?: SubjectCode
  gradeCode?: GradeCode
}>()

const { t } = useI18n()
const router = useRouter()
const { isSupported, isFullscreen, onFullscreenChange, setCurrentFullscreenState } = useFullScreen()
const { hasAccessRestricted } = storeToRefs(useLicenseControlStore())
const { buildResourceUrl, resolveBaseUrl } = useUrlBuilder()
const { isAuthenticated } = storeToRefs(useAuthStore())
const { hasLoaded: hasLoadedProducts } = storeToRefs(useProductStore())
const { getProductFromResource } = useProductStore()
const { isLegacyView, isAddonProduct } = useProductHelper()
const { isArchiveView } = useSubjectView()
const { selectedGrade } = storeToRefs(useFilterStore())
const { isTeacherArticle } = useContentHelper()

const {
  headers,
  headerViews,
  priorityHeader,
  isLoading,
} = usePathHeaders<ContentPackage>(
  computed(() => props.item),
  [
    ContentType.ProductHeader,
    ContentType.ProductPackage,
    ContentType.Folder,
    ContentType.Product,
  ],
  [
    ContentType.TeacherArticle,
  ]
)

const subjectCode = computed(() => props.subjectCode ?? product.value?.subjects[0])
const gradeCode = computed(() => props.gradeCode ?? selectedGrade.value ?? productGrades.value[0])

const product = computed(() => props.item && getProductFromResource(props.item))
const productUrl = computed(() => product.value && resolveBaseUrl(product.value, props.item, subjectCode.value, gradeCode.value))
const productGrades = computed(() => (product.value?.grades ?? []).sort(sortByGradeIndex))

const isSubjectView = computed(() => product.value && !isAddonProduct(product.value)
  && !isArchiveView(product.value, props.item))

const isProductView = computed(() => product.value && isAddonProduct(product.value)
  && !isLegacyView(product.value))

const isTeacherView = computed(() => props.item && isTeacherArticle(props.item))

const goToImmediateParent = computed(() => !(product.value && (
  (isSubjectView.value && !isTeacherView.value)
  || (isProductView.value && !isTeacherView.value)
)))

const immediateHeader = computed(() => goToImmediateParent.value
  ? [...headers.value].reverse()[0]
  : undefined)

const parentItem = computed(() => {
  if (!headerViews.value.length)
    return

  // Find any priority headers
  if (priorityHeader.value)
    return priorityHeader.value

  // Certain legacy items go to immediate parent
  return immediateHeader.value ?? headerViews.value[0]
})

const parentRoute = computed(() => {
  if (!parentItem.value)
    return
  const parentUrl = buildResourceUrl(parentItem.value)
  return router.resolve(parentUrl).name !== 'generic' ? parentUrl : undefined
})

const reportRoute = computed(() => {
  return router.currentRoute.value.meta.reportReturnPath
})

const isFavoriteNavigation = computed(() => !!router.currentRoute.value.params?.listId)

const favoriteListRoute = computed(() => {
  if (!isFavoriteNavigation.value)
    return undefined
  return router.resolve({
    name: 'favorite_list',
    params: { listId: router.currentRoute.value.params.listId }
  }).fullPath
})

const closeRoute = computed((): {
  name: string
  path: string
  quotes: boolean
} => {
  if (favoriteListRoute.value) {
    return {
      name: t('favorite.list'),
      path: favoriteListRoute.value,
      quotes: false,
    }
  }
  if (reportRoute.value) {
    return {
      name: t('report.button'),
      path: reportRoute.value,
      quotes: false,
    }
  }
  if (parentRoute.value) {
    return {
      name: parentItem.value?.title ?? '',
      path: parentRoute.value,
      quotes: true,
    }
  }
  if (productUrl.value) {
    return {
      name: product.value?.name ?? '',
      path: productUrl.value,
      quotes: true,
    }
  }
  return {
    name: t('home.title'),
    path: router.resolve({ name: 'home' }).href,
    quotes: false,
  }
})

const closeName = computed(() => {
  if (!closeRoute.value.name)
    return ''
  const name = closeRoute.value.name
    .replace('«', '‘')
    .replace('»', '’')
  return closeRoute.value.quotes
    ? t('backToQuote', { name })
    : t('backTo', { name: name.toLowerCase() })
})

const accessDenied = computed(() => hasAccessRestricted.value && props.item)

const showButton = computed(() => isAuthenticated.value && !accessDenied.value && !isFullscreen.value)

const isRoute = computed(() => !closeRoute.value.path.startsWith('https://'))

onMounted(() => {
  if (!isAuthenticated.value || !isSupported())
    return
  addEventListener('fullscreenchange', onFullscreenChange)
  setCurrentFullscreenState()
})

onUnmounted(() => {
  if (!isSupported())
    return
  removeEventListener('fullscreenchange', onFullscreenChange)
})
</script>

<template>
  <Tooltip
    v-if="showButton"
    :content="closeName"
    side="bottom"
    :disabled="!closeName"
  >
    <Button
      shape="square"
      size="xl"
      variant="outline"
      :aria-label="t('close')"
      :disabled="!hasLoadedProducts || isLoading"
      v-bind="isRoute ? { to: closeRoute.path } : { href: closeRoute.path }"
    >
      <font-awesome-icon :icon="faXmark" />
    </Button>
  </Tooltip>
</template>
